import React from 'react';
import { View, Box, Text, Stack, ButtonLink, Heading, VisuallyHidden, colors } from '@unibuddy/patron';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

var Card = function (_a) {
    var children = _a.children, activityStatus = _a.activityStatus;
    return (React.createElement(View, { position: "relative", borderRadius: "small-medium", overflow: "hidden", bgColor: "white", borderWidth: 1, borderColor: "grey150", w: "100%" },
        activityStatus,
        children));
};

var CardActivityStatus = function (_a) {
    var activityStatus = _a.activityStatus;
    return (React.createElement(Box, { position: "absolute", bgColor: "white", borderRadius: "full", top: 8, right: 8, padding: "xsmall" },
        React.createElement(View, { justifyContent: "center", alignItems: "center" },
            React.createElement(Text, { size: "xsmall" }, activityStatus))));
};

var DEFAULT_CDN_URL = "https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/";
var DEFAULT_FLAG_WIDTH = 32;
var DEFAULT_FLAG_HEIGHT = 24;
var DEFAULT_BORDER_WIDTH = 2;
var Flag = function (_a) {
    var alt = _a.alt, countryCode = _a.countryCode, _b = _a.width, width = _b === void 0 ? DEFAULT_FLAG_WIDTH : _b, _c = _a.height, height = _c === void 0 ? DEFAULT_FLAG_HEIGHT : _c, _d = _a.borderWidth, borderWidth = _d === void 0 ? DEFAULT_BORDER_WIDTH : _d, _e = _a.rounded, rounded = _e === void 0 ? true : _e;
    return (React.createElement(Box, { w: width + 2 * borderWidth, h: height + 2 * borderWidth, borderWidth: borderWidth, borderColor: "white", borderRadius: rounded ? "xxsmall" : undefined, overflow: "hidden", justifyContent: "center", alignItems: "center", display: "flex" },
        React.createElement("img", { alt: alt, src: "".concat(DEFAULT_CDN_URL).concat(countryCode.toLowerCase(), ".svg"), style: {
                // ALWAYS include a height on an svg - safari breaks without it
                height: height,
                objectFit: "fill",
            } })));
};

var BORDER_WIDTH = 2;
var AVATAR_IMAGE_SIZE = 108;
var AVATAR_CONTAINER_SIZE = AVATAR_IMAGE_SIZE + 2 * BORDER_WIDTH;
var CardAvatar = function (_a) {
    var url = _a.url, alt = _a.alt, primaryFlag = _a.primaryFlag, secondaryFlag = _a.secondaryFlag;
    return (React.createElement(Box, { flexShrink: 0, position: "relative" },
        React.createElement(Box, { w: AVATAR_CONTAINER_SIZE, h: AVATAR_CONTAINER_SIZE, bgColor: "white", borderRadius: "full", justifyContent: "center", alignItems: "center", display: "flex" },
            React.createElement("img", { alt: alt, src: url, style: {
                    width: AVATAR_IMAGE_SIZE,
                    height: AVATAR_IMAGE_SIZE,
                    borderRadius: "50%",
                    objectFit: "cover",
                } })),
        (primaryFlag || secondaryFlag) && (React.createElement(Box, { position: "absolute", bottom: 0, width: "100%" },
            React.createElement(Box, { display: "flex", justifyContent: "space-between", flexDirection: "row-reverse" },
                (secondaryFlag === null || secondaryFlag === void 0 ? void 0 : secondaryFlag.name) && (secondaryFlag === null || secondaryFlag === void 0 ? void 0 : secondaryFlag.code) && (React.createElement(Flag, { alt: "".concat(primaryFlag ? "Secondary" : "Primary", " country flag ").concat(secondaryFlag.name), countryCode: secondaryFlag.code })),
                (primaryFlag === null || primaryFlag === void 0 ? void 0 : primaryFlag.name) && (primaryFlag === null || primaryFlag === void 0 ? void 0 : primaryFlag.code) && (React.createElement(Flag, { alt: "Primary country flag ".concat(primaryFlag.name), countryCode: primaryFlag.code })))))));
};

var CardBody = function (_a) {
    var children = _a.children;
    return (React.createElement(View, { paddingX: "small-medium", paddingY: "medium", bgColor: "white", height: "100%", justifyContent: "space-between", gap: "small-medium" }, children));
};

var CardBodySection = function (_a) {
    var heading = _a.heading, content = _a.content;
    return (React.createElement(Stack, { space: "small" },
        heading && (
        // @ts-ignore
        React.createElement(Text, { as: "h3", size: "small", color: "grey600" }, heading)),
        typeof content === "string" ? (React.createElement(Text, { size: "small" }, content)) : (content)));
};

var CardBodySectionWrapper = function (_a) {
    var children = _a.children, _b = _a.space, space = _b === void 0 ? "small-medium" : _b;
    return React.createElement(Stack, { space: space }, children);
};

var CardButtonLink = function (_a) {
    var to = _a.to, _b = _a.color, color = _b === void 0 ? "default" : _b, _c = _a.ghost, ghost = _c === void 0 ? false : _c, handleClick = _a.handleClick, children = _a.children;
    return (React.createElement(ButtonLink, { block: true, round: true, ghost: ghost, color: color, to: to, onClick: function (e) {
            return handleClick(e);
        } }, children));
};

var CardHeader = function (_a) {
    var children = _a.children;
    return (React.createElement(View, { padding: "small-medium", bgColor: "grey50", gap: "small-medium", flexShrink: 0, overflow: "hidden" }, children));
};

var CardHeaderInfo = function (_a) {
    var buddyName = _a.buddyName, info = _a.info;
    return (
    // We need a surrounding View with height="100%" as
    // otherwise the stack cuts off some of the final row
    // of text.
    React.createElement(View, { h: "100%", minH: "100%", justifyContent: "center" },
        React.createElement(Stack, { space: "small" },
            React.createElement(Heading, { size: "xsmall", level: "2", truncate: true }, buddyName), info === null || info === void 0 ? void 0 :
            info.map(function (item, index) {
                var _a, _b;
                return (React.createElement(Text, { key: "".concat(buddyName, " info ").concat(index), size: "small", truncate: (_a = item.numberOfLines) !== null && _a !== void 0 ? _a : 2, color: (_b = item.color) !== null && _b !== void 0 ? _b : "grey900" }, item.text));
            }))));
};

var CardHeaderInfoWrapper = function (_a) {
    var children = _a.children;
    return (React.createElement(View, { flexDirection: "row", alignItems: "center", gap: "small-medium" }, children));
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */

function __makeTemplateObject(cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }
  return cooked;
}
typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

// Do not use {css} from styled components to create styles that are
// shared between the StyledAnchor and StyledLink. The current rollup
// config cannot expose {css} from styled-components in the cjs bundle,
// which results in tests that require cjs failing.
var StyledAnchor = styled.a(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  word-break: break-word;\n  text-decoration: underline;\n  color: ", ";\n\n  &:hover {\n    color: ", ";\n  }\n\n  &:active {\n    color: ", ";\n    opacity: 0.8;\n  }\n"], ["\n  word-break: break-word;\n  text-decoration: underline;\n  color: ", ";\n\n  &:hover {\n    color: ", ";\n  }\n\n  &:active {\n    color: ", ";\n    opacity: 0.8;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.CardLink.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.CardLink.hoverColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.CardLink.activeColor;
});
var StyledLink = styled(Link)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  word-break: break-word;\n  text-decoration: underline;\n  color: ", ";\n\n  &:hover {\n    color: ", ";\n  }\n\n  &:active {\n    color: ", ";\n    opacity: 0.8;\n  }\n"], ["\n  word-break: break-word;\n  text-decoration: underline;\n  color: ", ";\n\n  &:hover {\n    color: ", ";\n  }\n\n  &:active {\n    color: ", ";\n    opacity: 0.8;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.CardLink.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.CardLink.hoverColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.CardLink.activeColor;
});
var ThemedLink = React.memo(function (_a) {
    var to = _a.to, external = _a.external, onClick = _a.onClick, children = _a.children;
    return external ? (React.createElement(StyledAnchor, { href: to, onClick: onClick, rel: "noreferrer noopener", target: "_blank" },
        children,
        React.createElement(VisuallyHidden, null, "Opens in a new window"))) : (React.createElement(StyledLink, { to: to || "", onClick: onClick }, children));
});
var CardLink = function (_a) {
    var to = _a.to, handleClick = _a.handleClick, _b = _a.external, external = _b === void 0 ? false : _b, children = _a.children;
    return (React.createElement(Text, { size: "small" },
        React.createElement(ThemedLink, { to: to, onClick: function (e) {
                return handleClick(e);
            }, external: external }, children)));
};
var templateObject_1, templateObject_2;

var buddycardUITheme = {
    CardLink: {
        color: colors.blue700,
        hoverColor: colors.blue500,
        activeColor: colors.blue400,
    },
};

export { Card, CardActivityStatus, CardAvatar, CardBody, CardBodySection, CardBodySectionWrapper, CardButtonLink, CardHeader, CardHeaderInfo, CardHeaderInfoWrapper, CardLink, Flag, buddycardUITheme };
//# sourceMappingURL=index.js.map
